"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { useData } from "../../storage/accessors";
import Icon from "../../components/Icon";
import { AddTaskToListModal } from "../../components/AddTaskToListModal";
import ColorPicker from "../../components/lifepart/ColorPicker";
import { TaskListColumn } from "../../components/lists/TaskListColumn";
import {
    faPlus as add,
    faTrash as trashBin,
    faEdit as pencil,
    faArrowLeft as arrowBack,
    faArrowRight as arrowForward,
    faChevronUp as caretUp,
    faChevronDown as caretDown,
    faCheck as checkmark
} from '@fortawesome/free-solid-svg-icons';
import { TaskList as TaskListEntity } from "../../state/TaskList";
import { Task } from "../../state/Task";
import { useScreenSize } from "../../contexts/screenSizeContext";
import { useTaskModal } from "../../components/TaskModalContext";
import { useReadonly } from "../../contexts/readonlyContext";

// Модальное окно для создания нового списка
const CreateListModal: React.FC<{
    show: boolean;
    onHide: () => void;
    onSave: (name: string, color: string) => void;
    readonly: boolean;
}> = ({ show, onHide, onSave, readonly }) => {
    const [name, setName] = useState('');
    const [color, setColor] = useState('#007bff');

    const handleSave = () => {
        if (readonly || !name.trim()) return;
        onSave(name.trim(), color);
        setName('');
        setColor('#007bff');
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Новый список</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Название</Form.Label>
                    <Form.Control
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Введите название списка"
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Цвет</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                        <ColorPicker value={color} onChange={setColor} />
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Отмена</Button>
                <Button variant="outline-success" onClick={handleSave} disabled={readonly || !name.trim()}>
                    <Icon icon={add} /> Создать
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Модальное окно для редактирования списка
const EditListModal: React.FC<{
    show: boolean;
    onHide: () => void;
    taskList: TaskListEntity | null;
    onSave: (uuid: string, name: string, color: string) => void;
    onDelete: (uuid: string) => void;
    readonly: boolean;
}> = ({ show, onHide, taskList, onSave, onDelete, readonly }) => {
    const [name, setName] = useState(taskList?.name || '');
    const [color, setColor] = useState(taskList?.color || '#007bff');

    useEffect(() => {
        if (taskList) {
            setName(taskList.name);
            setColor(taskList.color);
        }
    }, [taskList]);

    const handleSave = () => {
        if (readonly || !taskList || !name.trim()) return;
        onSave(taskList.uuid, name.trim(), color);
        onHide();
    };

    const handleDelete = () => {
        if (readonly || !taskList) return;
        if (confirm('Удалить этот список? Задачи останутся в системе.')) {
            onDelete(taskList.uuid);
            onHide();
        }
    };

    if (!taskList) return null;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Редактировать список</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Название</Form.Label>
                    <Form.Control
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Цвет</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                        <ColorPicker value={color} onChange={setColor} />
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={readonly} variant="outline-danger" onClick={handleDelete}>
                    <Icon icon={trashBin} /> Удалить
                </Button>
                <Button variant="outline-secondary" onClick={onHide}>Отмена</Button>
                <Button variant="outline-success" onClick={handleSave} disabled={readonly || !name.trim()}>
                    <Icon icon={pencil} /> Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Модальное окно для сортировки списков (мобильная версия)
const SortListsModal: React.FC<{
    show: boolean;
    onHide: () => void;
    taskLists: TaskListEntity[];
    onReorder: (lists: TaskListEntity[]) => void;
    readonly: boolean;
}> = ({ show, onHide, taskLists, onReorder, readonly }) => {
    const [lists, setLists] = useState<TaskListEntity[]>([...taskLists]);

    useEffect(() => {
        setLists([...taskLists]);
    }, [taskLists]);

    const moveUp = (index: number) => {
        if (index <= 0) return;
        const newLists = [...lists];
        [newLists[index - 1], newLists[index]] = [newLists[index], newLists[index - 1]];
        setLists(newLists);
    };

    const moveDown = (index: number) => {
        if (index >= lists.length - 1) return;
        const newLists = [...lists];
        [newLists[index], newLists[index + 1]] = [newLists[index + 1], newLists[index]];
        setLists(newLists);
    };

    const handleSave = () => {
        if (readonly) return;
        const updatedLists = lists.map((list, index) => ({ ...list, order: index }));
        onReorder(updatedLists);
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Сортировка списков</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex flex-column gap-2">
                    {lists.map((list, index) => (
                        <div
                            key={list.uuid}
                            className="d-flex align-items-center gap-2 p-2 border rounded bg-body-tertiary"
                        >
                            <div
                                className="rounded me-1"
                                style={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: list.color,
                                    flexShrink: 0
                                }}
                            />
                            <span className="flex-grow-1">{list.name}</span>
                            <div className="d-flex gap-1">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={index === 0 || readonly}
                                    onClick={() => moveUp(index)}
                                >
                                    <Icon icon={caretUp} size="sm" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={index === lists.length - 1 || readonly}
                                    onClick={() => moveDown(index)}
                                >
                                    <Icon icon={caretDown} size="sm" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Отмена</Button>
                <Button variant="outline-success" onClick={handleSave} disabled={readonly}>
                    <Icon icon={checkmark} /> Сохранить порядок
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Модальное окно для перемещения задачи между списками (мобильная версия)
const MoveTaskModal: React.FC<{
    show: boolean;
    onHide: () => void;
    task: Task | null;
    taskLists: TaskListEntity[];
    onMove: (taskUuid: string, targetListUuid: string) => void;
    readonly: boolean;
}> = ({ show, onHide, task, taskLists, onMove, readonly }) => {
    const { tasks } = useData();

    const handleMove = (targetListUuid: string) => {
        if (readonly) return;
        if (task) {
            tasks.save(task.uuid, { taskList: targetListUuid });
            onHide();
        }
    };

    if (!task) return null;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Переместить задачу</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-2">
                    <strong>Задача:</strong> {task.description}
                </div>
                <div className="d-flex flex-column gap-2">
                    {taskLists.map(list => (
                        <Button
                            key={list.uuid}
                            variant="outline-primary"
                            onClick={() => handleMove(list.uuid)}
                            className="text-start"
                            disabled={readonly}
                        >
                            <div
                                className="rounded me-2 d-inline-block"
                                style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: list.color
                                }}
                            />
                            {list.name}
                        </Button>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Отмена</Button>
            </Modal.Footer>
        </Modal>
    );
};

// Иконка для кнопки уже импортирована выше (checkmark)

// Основная страница списков
export default function ListsPage() {
    const { taskLists, tasks, roles } = useData();
    const { readonly } = useReadonly();
    const lifeParts = Object.values(roles.objects);
    const navigate = useNavigate();
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const { setTaskModal } = useTaskModal() || {};

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingList, setEditingList] = useState<TaskListEntity | null>(null);
    const [showSortModal, setShowSortModal] = useState(false);
    const [movingTask, setMovingTask] = useState<Task | null>(null);
    const [activeListIndex, setActiveListIndex] = useState(0);

    const handleEditList = (taskList: TaskListEntity) => {
        setEditingList(taskList);
    };

    // Drag & Drop для списков (десктоп)
    const handleListDragStart = (e: React.DragEvent, listUuid: string) => {
        if (isMobile) return;
        e.dataTransfer.setData('listUuid', listUuid);
    };

    const handleListDragOver = (e: React.DragEvent, index: number) => {
        if (isMobile) return;
        e.preventDefault();
    };

    const handleListDrop = (e: React.DragEvent, targetIndex: number) => {
        if (isMobile) return;
        e.preventDefault();

        const sourceListUuid = e.dataTransfer.getData('listUuid');
        if (!sourceListUuid) return;

        // Получаем актуальный список прямо из стейта
        const currentLists = Object.values(taskLists.objects).sort((a, b) => (a.order || 0) - (b.order || 0));
        
        const newLists = [...currentLists];
        const sourceIndex = newLists.findIndex(l => l.uuid === sourceListUuid);
        const targetIndexInArray = targetIndex;
        
        if (sourceIndex === -1 || sourceIndex === targetIndexInArray) return;

        const draggedItem = newLists[sourceIndex];
        newLists.splice(sourceIndex, 1);
        newLists.splice(targetIndexInArray, 0, draggedItem);

        // Обновляем порядок
        newLists.forEach((list, index) => {
            taskLists.save(list.uuid, { order: index });
        });
    };

    const handleListDragEnd = () => {
        // Очистка данных
    };

    // Получаем все списки, отсортированные по порядку
    const sortedLists = useMemo(() => {
        return Object.values(taskLists.objects).sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [taskLists.objects]);

    // Получаем задачи для каждого списка
    const getTasksForList = useCallback((listUuid: string) => {
        return Object.values(tasks.objects)
            .filter(t => t.taskList === listUuid)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [tasks.objects]);

    const handleCreateList = (name: string, color: string) => {
        if (readonly) return;
        const newList: TaskListEntity = {
            uuid: crypto.randomUUID(),
            name,
            color,
            order: sortedLists.length
        };
        taskLists.create(newList);
    };

    const handleUpdateList = (uuid: string, name: string, color: string) => {
        if (readonly) return;
        taskLists.save(uuid, { name, color });
    };

    const handleDeleteList = (uuid: string) => {
        if (readonly) return;
        // Очищаем taskList у всех задач этого списка
        Object.values(tasks.objects).forEach(task => {
            if (task.taskList === uuid) {
                tasks.save(task.uuid, { taskList: '' });
            }
        });
        taskLists.delete(uuid);
    };

    const handleReorderLists = (lists: TaskListEntity[]) => {
        if (readonly) return;
        lists.forEach(list => {
            taskLists.save(list.uuid, { order: list.order });
        });
    };

    const handleRemoveTaskFromList = (taskUuid: string) => {
        if (readonly) return;
        tasks.save(taskUuid, { taskList: '' });
    };

    const handleMoveTask = (taskUuid: string) => {
        if (readonly) return;
        const task = tasks.objects[taskUuid];
        if (task) {
            setMovingTask(task);
        }
    };

    const handleDropTask = (taskUuid: string, sourceListUuid: string, targetListUuid: string) => {
        if (readonly) return;
        // Перемещаем задачу в целевой список (в конец)
        if (sourceListUuid !== targetListUuid) {
            const targetTasks = Object.values(tasks.objects)
                .filter(t => t.taskList === targetListUuid)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
            const newOrder = targetTasks.length;
            tasks.save(taskUuid, { taskList: targetListUuid, order: newOrder });
        }
    };

    // Обновление порядка задач внутри списка или перемещение между списками
    const handleReorderTasks = (taskUuid: string, targetIndex: number, listUuid: string) => {
        if (readonly) return;
        const task = tasks.objects[taskUuid];
        if (!task) return;

        const currentListUuid = task.taskList || '';
        const isMovingBetweenLists = currentListUuid !== listUuid;

        if (isMovingBetweenLists) {
            // Перемещаем задачу из другого списка
            const targetTasks = Object.values(tasks.objects)
                .filter(t => t.taskList === listUuid)
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            const newOrder = [...targetTasks.map(t => t.uuid)];
            newOrder.splice(targetIndex, 0, taskUuid);

            // Обновляем порядок всех задач в целевом списке
            newOrder.forEach((uuid, index) => {
                tasks.save(uuid, { order: index, taskList: listUuid });
            });
        } else {
            // Переупорядочивание внутри того же списка
            const currentListTasks = Object.values(tasks.objects)
                .filter(t => t.taskList === listUuid)
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            const currentIndex = currentListTasks.findIndex(t => t.uuid === taskUuid);
            if (currentIndex !== -1 && currentIndex !== targetIndex) {
                const newOrder = [...currentListTasks.map(t => t.uuid)];
                const [removed] = newOrder.splice(currentIndex, 1);
                newOrder.splice(targetIndex, 0, removed);

                newOrder.forEach((uuid, index) => {
                    tasks.save(uuid, { order: index });
                });
            }
        }
    };

    // Навигация для мобильной версии
    const goPreviousList = () => {
        if (activeListIndex > 0) {
            setActiveListIndex(activeListIndex - 1);
        }
    };

    const goNextList = () => {
        if (activeListIndex < sortedLists.length - 1) {
            setActiveListIndex(activeListIndex + 1);
        }
    };

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h2 className="mb-0">Списки</h2>
                            <p className="text-muted mb-0">Управляйте списками задач</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button disabled={readonly} variant="outline-success" onClick={() => setShowCreateModal(true)}>
                                <Icon icon={add} /> Новый список
                            </Button>
                            {isMobile && sortedLists.length > 1 && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowSortModal(true)}
                                >
                                    Сортировка
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {lifeParts.length === 0 ? (
                <Alert variant="warning">
                    Сначала создайте части жизни на экране «Баланс» <Link className="btn btn-primary btn-sm" to="./roles">Перейти</Link>
                </Alert>
            ) : sortedLists.length === 0 ? (
                <Alert variant="info">
                    <h5>Списков пока нет</h5>
                    <p>Создайте первый список, чтобы начать управлять задачами</p>
                    <Button disabled={readonly} variant="outline-success" onClick={() => setShowCreateModal(true)}>
                        <Icon icon={add} /> Создать список
                    </Button>
                </Alert>
            ) : (
                <>
                    {/* Мобильная версия: переключатель списков */}
                    {isMobile && sortedLists.length > 1 && (
                        <Row className="mb-3">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goPreviousList}
                                        disabled={activeListIndex === 0}
                                    >
                                        <Icon icon={arrowBack} />
                                    </Button>
                                    <div className="text-center">
                                        <strong>{activeListIndex + 1} / {sortedLists.length}</strong>
                                        <div className="text-muted">
                                            {sortedLists[activeListIndex]?.name}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goNextList}
                                        disabled={activeListIndex === sortedLists.length - 1}
                                    >
                                        <Icon icon={arrowForward} />
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Список колонок */}
                    <Row className="gx-3">
                        {sortedLists.map((list, index) => (
                            <Col
                                key={list.uuid}
                                style={{
                                    minWidth: isMobile ? '100%' : '330px',
                                    maxWidth: isMobile ? '100%' : '400px',
                                    transition: 'opacity 0.2s ease'
                                }}
                            >
                                <TaskListColumn
                                    taskList={list}
                                    tasks={getTasksForList(list.uuid)}
                                    onRemoveTask={handleRemoveTaskFromList}
                                    onMoveTask={handleMoveTask}
                                    onDropTask={handleDropTask}
                                    onReorderTasks={handleReorderTasks}
                                    onEditList={handleEditList}
                                    onOpenTask={(taskUuid: string) => setTaskModal?.({ taskUUID: taskUuid })}
                                    isMobile={isMobile}
                                    isActive={isMobile ? index === activeListIndex : true}
                                    onDragStart={!isMobile ? (e: React.DragEvent) => handleListDragStart(e, list.uuid) : undefined}
                                    onDragOver={!isMobile ? (e: React.DragEvent) => {
                                        if (isMobile) return;
                                        e.preventDefault();
                                    } : undefined}
                                    onDrop={!isMobile ? (e: React.DragEvent) => {
                                        const dataType = e.dataTransfer.getData('dragType');
                                        if (dataType === 'reorder') return;
                                        handleListDrop(e, index);
                                    } : undefined}
                                    readonly={readonly}
                                />
                            </Col>
                        ))}
                    </Row>
                </>
            )}

            {/* Модальные окна */}
            <CreateListModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSave={handleCreateList}
                readonly={readonly}
            />

            {editingList && (
                <EditListModal
                    show={!!editingList}
                    onHide={() => setEditingList(null)}
                    taskList={editingList}
                    onSave={handleUpdateList}
                    onDelete={handleDeleteList}
                    readonly={readonly}
                />
            )}

            {isMobile && (
                <>
                    <SortListsModal
                        show={showSortModal}
                        onHide={() => setShowSortModal(false)}
                        taskLists={sortedLists}
                        onReorder={handleReorderLists}
                        readonly={readonly}
                    />

                    <MoveTaskModal
                        show={!!movingTask}
                        onHide={() => setMovingTask(null)}
                        task={movingTask}
                        taskLists={sortedLists}
                        readonly={readonly}
                        onMove={(taskUuid, listUuid) => {
                            tasks.save(taskUuid, { taskList: listUuid });
                            setMovingTask(null);
                        }}
                    />
                </>
            )}
        </>
    );
}